<template>
  <div class="store-locator__map__google-map" ref="google-map"></div>
</template>

<script>
import { createScript, loadScripts } from '../helpers/loader';
import template from '../helpers/template';

let loading = false;

const DEFAULTS = {
  callback: '__google_maps_api_provider__', //
  key: null, // Api key
  libraries: [], // ['geometry', 'places']
  client: null, // 'yourclientkey'
  version: '3.18', // '3.18'
  channel: null, //
  language: null, // 'fr'
  region: null, // 'GB'
};

export default {
  name: 'cp-google-map',
  props: {
    options: {
      required: true,
      type: Object,
    },
    stores: {
      required: true,
      type: Array,
    },
  },
  data() {
    return {
      circle: null,
      clusters: null,
      infoWindows: {},
      location: null,
      map: null,
      markers: [],
    };
  },
  watch: {
    /**
     * On stores updates: reset markers, clusters and unactive selected marker.
     * @param stores
     */
    stores(stores) {
      this.createMarkers(stores);

      if (this.clusters) {
        this.createClusters();
      }

      this.$bus.emit('disable-marker');
    },
  },
  methods: {
    /**
     * Active a marker and open his info window.
     * @param uid
     */
    activeMarker(stores, uid) {
      // var result = stores.filter((obj) => {
      //   return obj.uid === uid;
      // });
      // console.log('-----', result[0]);
      // this.infoWindows[uid]();
    },

    /**
     * Center map to the given lat, lng and set the zoom level.
     * @param lat
     * @param lng
     * @param zoom
     */
    centerMap(lat, lng, zoom = null) {
      zoom = zoom || this.zoom;

      this.map.setZoom(this.zoom);
      this.map.setCenter(new window.google.maps.LatLng(lat, lng));
      this.map.setZoom(zoom);
    },

    /**
     * If exists, remove the radius circle from the map.
     */
    clearCircle() {
      if (this.circle) {
        this.circle.setMap(null);
        this.circle = null;
      }
    },

    /**
     * If exists, remove all clusters from the map.
     */
    clearClusters() {
      if (this.clusters) {
        this.clusters.clearMarkers();
      }
    },

    /**
     * If exists, remove the current location marker from the map.
     */
    clearCurrentMarker() {
      if (this.location) {
        this.location.setMap(null);
        this.location = null;
      }
    },

    /**
     * Remove all markers from the map.
     * @param remove
     */
    clearMarkers(remove = false) {
      this.markers.map((marker) => marker.setMap(null));

      if (remove) {
        this.markers = [];
      }
    },

    /**
     * Create a circle radius on the given lat, lng with the given radius.
     * @param lat
     * @param lng
     * @param radius
     */
    createCircle(lat, lng, radius = 5000) {
      const options = {
        center: new window.google.maps.LatLng(lat, lng),
        fillColor: '#213B87',
        fillOpacity: 0.35,
        map: this.map,
        radius,
        strokeColor: '#213B87',
        strokeOpacity: 0.8,
        strokeWeight: 2,
      };

      if (this.circle) {
        this.circle.setMap(null);
      }

      this.circle = new window.google.maps.Circle(options);
      window.google.maps.event.addListener(this.circle, 'click', () =>
        this.$bus.emit('disable-marker')
      );
    },

    /**
     * Create clusters on the map.
     */
    createClusters() {
      if (this.clusters) {
        this.clearClusters();
      }

      // @see https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/markerclusterer.js
      this.clusters = new window.MarkerClusterer(
        this.map,
        this.markers,
        this.options.clusters
      );
    },

    /**
     * Create a current location marker on given lat, lng.
     * @param lat
     * @param lng
     */
    createCurrentMarker(lat, lng) {
      if (this.location) {
        this.location.setMap(null);
      }

      this.location = this.createMarker(lat, lng);
    },

    /**
     * Instantiate the map.
     */
    createMap() {
      if (this.map) {
        return;
      }

      const options = Object.assign(
        {
          infoWindow: new window.google.maps.InfoWindow({ maxWidth: 500 }),
        },
        this.options.options
      );

      if (window.google) {
        this.map = new window.google.maps.Map(
          this.$refs['google-map'],
          options
        );

        window.google.maps.event.addListener(this.map, 'click', () =>
          this.$bus.emit('disable-marker')
        );
      } else {
        throw new Error('[store-locator] Google Map API is not loaded!');
      }
    },

    /**
     * Create a marker on the given lat, lng.
     * @param lat
     * @param lng
     * @param options
     * @return {window.google.maps.Marker}
     */
    createMarker(lat, lng, options = {}) {
      options = {
        icon: this.options.location.icon,
        map: this.map,
        position: new window.google.maps.LatLng(lat, lng),
        ...options,
      };

      return new window.google.maps.Marker(options);
    },

    /**
     * Create all markers from the given stores.
     * @param stores
     */
    createMarkers(stores) {
      if (!Array.isArray(stores)) {
        stores = [stores];
      }

      if (this.markers.length) {
        this.clearMarkers(true);
      }

      this.markers = stores.map((store) => {
        const { lat, lng } = store;
        const uid = this.$action.uid({ lat, lng, title: store.title });
        const icon = store.marker ? store.marker : this.options.markers.icon;

        let markerOptions = {
          icon: typeof icon === 'string' ? icon : icon.inactive,
          id: uid,
          title: store.title,
        };

        if (typeof this.getConfig('formatMarker') === 'function') {
          markerOptions = this.getConfig('formatMarker')(store, markerOptions);
        }

        // @note - look https://github.com/scottdejonge/map-icons
        const marker = this.createMarker(lat, lng, markerOptions);

        this.infoWindows[uid] = () => {
          let content = template(this.options.infoWindowTemplate, store);

          if (typeof this.getConfig('formatInfoWindow') === 'function') {
            content = this.getConfig('formatInfoWindow')(
              store,
              this.options.infoWindowTemplate
            );
          }

          this.$bus.emit('disable-marker');

          marker.setIcon(typeof icon === 'string' ? icon : icon.active);

          this.map.infoWindow.setContent(content);
          this.map.infoWindow.open(this.map, marker);
        };

        this.$bus.on('disable-marker', () =>
          marker.setIcon(typeof icon === 'string' ? icon : icon.inactive)
        );
        window.google.maps.event.addListener(marker, 'click', () =>
          this.$bus.emit('active-marker', marker.id)
        );

        return marker;
      });
    },

    /**
     * Display only markers inside a radius starting on the given lat, lng with a radius of the given distance.
     * @param lat
     * @param lng
     * @param distance
     */
    displayMarkers(lat, lng, distance = 5000) {
      const selected = this.markers.reduce((acc, marker) => {
        const position = marker.getPosition();
        const distanceBetween =
          window.google.maps.geometry.spherical.computeDistanceBetween(
            position,
            new window.google.maps.LatLng(lat, lng)
          );
        let visibility = true;

        if (distance) {
          visibility = distance >= distanceBetween;
        }

        marker.setVisible(visibility);

        if (visibility) {
          acc.push(marker);
        }

        return acc;
      }, []);

      this.$bus.emit(
        'set-displayed-markers',
        selected.map((item) => item.id)
      );
    },

    /**
     * Geolocate user on load.
     */
    geolocateCenter() {
      if (this.options.geocode && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.centerMap(
            position.coords.latitude,
            position.coords.longitude,
            12
          );
          this.createCircle(
            position.coords.latitude,
            position.coords.longitude
          );
          this.createMarker(
            position.coords.latitude,
            position.coords.longitude
          );
        }, console.error);
      }
    },

    /**
     * Load Google Maps Api and dependencies
     * @param options
     */
    loadGoogleMapsApi(options) {
      if (window.google) {
        return window.google;
      }

      this.$action.loading(true);

      new Promise((resolve, reject) => {
        if (window.google) {
          resolve(window.google);
        } else if (loading) {
          let interval = setInterval(() => {
            if (window.google) {
              interval && clearInterval(interval);

              loading = false;
              resolve(window.google);
            }
          }, 10);
        } else {
          loading = false;

          options = Object.assign({}, DEFAULTS, options);

          window[options.callback] = () => {
            loading = false;
            resolve(window.google);
          };

          const params = Object.keys(options)
            .map(
              (key) =>
                encodeURIComponent(key) + '=' + encodeURIComponent(options[key])
            )
            .join('&');

          const url = 'https://maps.googleapis.com/maps/api/js?' + params;

          createScript(url, null, reject);
        }
      }).then((google) => {
        loadScripts(this.getConfig('dependencies'), () => {
          this.$bus.emit('api-loaded', google);
          this.$action.loading(false);
        });
      });
    },
  },
  mounted() {
    this.trace('{google-map.vue} mounted');

    this.$bus.on('disable-marker', () => this.map.infoWindow.close());
    this.$bus.on('active-marker', (uid) => this.activeMarker(this.stores, uid));
    this.$bus.on('center-map', (lat, lng, zoom) =>
      this.centerMap(lat, lng, zoom)
    );
    this.$bus.on('clear-circle', () => this.clearCircle());
    this.$bus.on('clear-current-marker', () => this.clearCurrentMarker());
    this.$bus.on('create-circle', (lat, lng, radius) =>
      this.createCircle(lat, lng, radius)
    );
    this.$bus.on('create-current-marker', (lat, lng, options) =>
      this.createCurrentMarker(lat, lng, options)
    );
    // this.$bus.on('display-markers', (lat, lng, distance) =>
    //   this.displayMarkers(lat, lng, distance)
    // );

    this.$bus.on('api-loaded', () => {
      this.createMap();
      this.createMarkers(this.stores);

      if (this.options.clusters) {
        this.createClusters();
      }

      if (this.options.geocode) {
        this.geolocateCenter();
      }
    });

    this.loadGoogleMapsApi(this.getConfig('api'));
  },
};
</script>
